import { CommonModule, isPlatformBrowser, NgOptimizedImage, NgStyle, PlatformLocation } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  Signal,
  signal,
  TransferState,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { isReadable, TinyColor } from '@ctrl/tinycolor';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBars, faTimes, faPlay, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Subscription } from 'rxjs';

import {
  ENVIRONMENT,
  MovieSiteConfigAspectRatio,
  MovieSiteConfigAwarenessLogoPosition,
  MovieSiteHeaderImagesConfig,
  MovieSiteHeaderImg
} from '@environment';
import { FeatureGalleryCarouselComponent } from '@usheru-hff/movie-detail/feature-gallery-carousel';
import { MovieDetailUiMovieElementsModule } from '@usheru-hff/movie-detail/ui-movie-elements';
import { NewsFeatureNewsCarouselModule } from '@usheru-hff/news/feature-news-carousel';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MOVIE_TRANSFER_KEY, MovieService } from '@usheru-hff/sdk/movie';
import { attachTrackingToDescription, TrackingService } from '@usheru-hff/sdk/tracking';
import { getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { SharedFeatureCalendarButtonComponent } from '@usheru-hff/shared/feature-calendar-button';
import { SharedFeatureSidemenuComponent } from '@usheru-hff/shared/feature-sidemenu';
import { SharedFeatureSocialLinksModule } from '@usheru-hff/shared/feature-social-links';
import { IntersectionObserverService, IntersectionObserverDirective } from '@usheru-hff/shared/intersection-observable';
import { VideoComponent, VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { getDateFromDDMMYYYY, SharedUtilsModule } from '@usheru-hff/shared/utils';
import { ImageService, SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TitleService } from '@usheru-hff/shared/utils-title';
import { SharedUtilsTranslationModule, TranslationsService } from '@usheru-hff/shared/utils-translation';

import { BrandMovieService } from '../brand-movie.service';

type StatusType = 'book-now' | 'calendar' | 'watch-now';

@Component({
  selector: 'feature-movie-awareness-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgOptimizedImage,
    MovieDetailUiMovieElementsModule,
    SharedFeatureSocialLinksModule,
    FeatureGalleryCarouselComponent,
    FontAwesomeModule,
    SharedFeatureCalendarButtonComponent,
    SharedFeatureSidemenuComponent,
    VideoComponent,
    NewsFeatureNewsCarouselModule,
    IntersectionObserverDirective,
    SharedUtilsTranslationModule,
    SharedUtilsImageModule,
    SharedUtilsModule
  ],
  templateUrl: './feature-movie-awareness-page.component.html',
  styleUrls: ['./feature-movie-awareness-page.component.scss']
})
export class FeatureMovieAwarenessPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() movie: Movie | undefined;

  protected readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly PLATFORM_ID = inject(PLATFORM_ID);
  private readonly isPlatformBrowser = isPlatformBrowser(this.PLATFORM_ID);
  private readonly platformLocation = inject(PlatformLocation);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly channelService = inject(ChannelService);
  private readonly movieService = inject(MovieService);
  private readonly brandMovieService = inject(BrandMovieService);
  private readonly trackingService = inject(TrackingService);
  private readonly intersectionObserve = inject(IntersectionObserverService);
  private readonly videoService = inject(VideoFullscreenService);
  private readonly metaTagsService = inject(MetatagsService);
  private readonly imgService = inject(ImageService);
  private readonly translationsService = inject(TranslationsService);
  private readonly showtimeStreamingService = inject(ShowtimeStreamingService);
  private readonly titleService = inject(TitleService);
  private readonly env = inject(ENVIRONMENT);
  private readonly transferState = inject(TransferState);

  readonly logoExternalLink: string | undefined = this.env?.movieAwarenessLogoExternalLink;

  @HostBinding('style') bindingStyle: {
    [rule: string]: string;
  } = {};
  @ViewChildren('section') sectionsQueryList!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren(IntersectionObserverDirective, { read: ElementRef<HTMLElement> }) intersections!: QueryList<
    ElementRef<HTMLElement>
  >;
  @ViewChildren(IntersectionObserverDirective) intersectionsDirectives!: QueryList<IntersectionObserverDirective>;
  @ViewChild(SharedFeatureSidemenuComponent) sidemenuComponent!: SharedFeatureSidemenuComponent;
  @ViewChild('storyline') sectionStorylineRef!: ElementRef<HTMLElement>;
  @ViewChild('gallery') sectionGalleryRef!: ElementRef<HTMLElement>;
  @ViewChild('footerWrapper') footerWrapperRef!: ElementRef<HTMLElement>;
  @ViewChild('descriptionContainer', { static: false }) descriptionContainerRef!: ElementRef;

  /*   movie: Signal<Movie | null | undefined>; */
  status: Signal<StatusType[]>;
  releaseDate: Signal<Date | undefined>;
  channelName = signal<string | undefined>(undefined);
  channelLogoUrl = '';
  lastScrollTop = 0;
  brandedMovieStyle = signal({});
  isBrandColorReadable = signal<boolean>(true);
  sectionStorylineVisibleClass = signal<string[]>([]);
  readonly desktopBreakpoint = 1200;
  readonly visibilityAnimatonClass = 'ng-visible';
  readonly sectionStorylineEnterThreshold = 20;
  sidebarVisible1 = false;
  auxiliarMenuVisible = signal<boolean>(false);
  mainMenuVisibleOutput = new BehaviorSubject<boolean>(false);
  mainMenuVisibleOutput$!: Subscription;
  stylesForGradient = signal<unknown>({});
  sectionsHyperLink = signal<SectionHyperLink[]>([
    {
      label: 'movie.menu.home',
      placeholder: 'Home',
      ref: 'home'
    },
    {
      label: 'movie.menu.about',
      placeholder: 'About',
      ref: 'storyline'
    }
  ]);
  landingFillScreen = signal<boolean>(true);
  customHeaderImages = signal<MovieSiteHeaderImg[] | undefined>(undefined);

  today = new Date().getTime();
  observers?: string[];
  routePath = '.';
  gallerySlidesPerView = {
    0: 1.2,
    480: 1.2,
    1024: 2.2,
    1400: 3
  };

  appHasOnlyMovies = false;
  readonly TrackingCategory = 'MovieDetail';
  readonly faPlay = faPlay;
  readonly faMenu = faBars;
  readonly faClose = faTimes;
  readonly faGlobe = faGlobe;

  readonly movieAwarenessBackgroundImageFit = this.env.movieAwarenessBackgroundImageFit;

  hideLogoTitle: boolean | undefined = false;
  hideTitleText: boolean | undefined = false;
  hideReleaseMessage: boolean | undefined = false;
  attachReleaseMessageToLogo: boolean | undefined = false;
  disableGradient = false;
  disableImdb = false;
  disableTmdb = false;

  readonly movieShowtimeLink: string | undefined;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.intersectionObserve.checkBreakpoints();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    // this.setFixTopMovieImageForMobile();
  }

  get element() {
    return this.elementRef.nativeElement;
  }

  readonly ASSETSLOCKER_LABEL = 'movie-site.assets-locker-label';
  readonly ASSETSLOCKER_LABEL_BUTTON = 'movie-site.assets-locker';

  constructor() {
    if (this.isPlatformBrowser) {
      window.scroll({ top: 0, behavior: 'instant' });
      this.movieShowtimeLink = window.location.href.replace('/film-info', '/book-now').split('#')[0];
    }

    /**
     * If for some reason the user reach the route without the awarness path. It will be added
     * to the url and the user will be redirected to the correct path.
     */
    // if (this.route.snapshot.url[0]?.path !== 'film-info') {
    //   this.router.navigate([this.router.url, 'film-info'], {
    //     replaceUrl: true
    //  });
    // }

    /*    this.movie = toSignal(this.movieService.movie$);
    effect(() => {

    }); */
    this.appHasOnlyMovies = this.env?.appHasOnlyMovies || false;
    // this.brandMovieService.checkMovieInRouteState();

    this.status = computed(() => {
      const status: StatusType[] = [];
      const locReleaseDate = this.movie?.locReleaseDate;
      const releaseDate = this.movie?.releaseDate;
      if (!locReleaseDate && !releaseDate) return [];

      const release = getDateFromDDMMYYYY(locReleaseDate! || releaseDate!).getTime();

      if (release > this.today) {
        status.push('calendar');
      }

      if (this.hasShowtimes) {
        status.push('book-now');
      } else if (this.hasStreaming) {
        status.push('watch-now');
      }
      return status;
    });

    this.releaseDate = computed(() => {
      const release = this.movie?.locReleaseDate || this.movie?.releaseDate;
      return release ? getDateFromDDMMYYYY(release) || undefined : undefined;
    });
    //

    //
    this.setChannelLogo();
    //
    // check if the route ends with 'film-info'
    const url = this.route.snapshot.url;
    this.routePath = url[url.length - 1].path === 'film-info' ? '..' : '.';
  }

  ngOnInit() {
    if (this.movie) this.brandMovieService.setMetatags(this.movie!);

    this.injectStyles();
    this.bindingStyle = this.setColorStyles();
    this.setEnvConfiguration();

    this.isBrandColorReadable.set(this.isRedeableBrandOverBackgroundColor());

    this.setSectionsHyperlink();
  }

  ngAfterViewInit() {
    this.scrollTop();
    this.setServiceSettings();
    this.appendFooter();
    this.checkAnchor();
    if (this.isPlatformBrowser) {
      this.activeIntersectionAnimation();
    }
    this.setAuxiliarMenuVisiblility();
    if (this.movie?.faviconImageUrl) {
      this.titleService.setFavicon(this.movie?.faviconImageUrl);
    }

    attachTrackingToDescription(this.trackingService, 'click', this.descriptionContainerRef);
  }

  scrollTop() {
    if (this.isPlatformBrowser) {
      this.elementRef.nativeElement.scroll({ top: 0, behavior: 'smooth' });
    }
  }

  // component.ts
  containerStyle: { [prop: string]: string } = {
    width: '100vw',
    height: '100vh'
  };

  onImageLoad(image: HTMLImageElement) {
    const imageWidth = image.naturalWidth; // e.g., 1481
    const imageHeight = image.naturalHeight; // e.g., 833
    const imageRatio = imageWidth / imageHeight; // ~1.7787

    // Container ratio if we did 100vw x 100vh
    const containerRatio = window.innerWidth / window.innerHeight;
    const ratioDelta = imageRatio / containerRatio;

    // Some threshold to decide if we're drastically cropping
    const THRESHOLD = 1.2;

    if (ratioDelta > THRESHOLD) {
      // Image is significantly "wider" than container
      // Keep container full width = 100vw, but adjust height so ratio matches
      // height = (100vw / imageRatio)
      // But we can't directly do: 'height: `calc(100vw / 1.7787)`' unless we convert 1.7787 into a fraction.
      // In plain CSS we can do `calc(100vw / 1.7787)`, but let's be dynamic in JS:

      this.containerStyle = {
        width: '100vw',
        height: `calc(100vw / ${imageRatio})`
      };
    } else if (ratioDelta < 1 / THRESHOLD) {
      // Image is significantly "taller" than container
      // Keep container full height = 100vh, but adjust width so ratio matches
      // width = (100vh * imageRatio)
      this.containerStyle = {
        width: `calc(100vh * ${imageRatio})`,
        height: '100vh'
      };
    } else {
      // Ratios are close enough -- stick with 100vw x 100vh
      this.containerStyle = {
        width: '100vw',
        height: '100vh'
      };
    }
  }

  setSectionsHyperlink() {
    if (this.movie?.movieImages) {
      this.sectionsHyperLink.update(hyperlinks => {
        hyperlinks.push({
          label: 'movie-template-a.menu.gallery',

          placeholder: 'Gallery',

          ref: 'gallery'
        });

        return hyperlinks;
      });
    }
  }

  setChannelLogo() {
    this.channelName.set(this.channelService.channel.name);
    this.channelLogoUrl = this.channelService.channel.logoUrl;
  }

  setServiceSettings() {
    this.brandMovieService.sidemenuRef = this.sidemenuComponent;
  }

  setColorStyles() {
    const brandColor = this.movie?.settings?.movieSiteBrandColor || '#000000';
    const backgroundColor = this.movie?.settings?.movieSiteBackgroundColor || '#ffffff';
    const borderRadious = this.movie?.settings?.selectShowtimeContainerBorderRadius || '12px';
    return this.brandMovieService.setColorStyles(brandColor, backgroundColor, borderRadious);
  }

  injectStyles() {
    if (!this.isPlatformBrowser) return;
    const fontFamily = this.movie?.settings?.movieSiteNormalFontFile || 'Arial';
    const fontFamilyBold = this.movie?.settings?.movieSiteBoldFontFile || 'Arial';
    this.brandMovieService.injectStyles(fontFamily, fontFamilyBold);
  }

  appendFooter() {
    this.brandMovieService.moveFooter(this.footerWrapperRef.nativeElement);
    this.brandMovieService.injectCustomFooterStyle(`
        ush-footer {
          width: 100%;
        }
      `);
  }

  setAuxiliarMenuVisiblility() {
    if (this.isPlatformBrowser) {
      this.mainMenuVisibleOutput$ = this.mainMenuVisibleOutput.subscribe(value => {
        this.auxiliarMenuVisible.set(!value);
      });
    }
  }

  checkAnchor() {
    this.brandMovieService.checkAnchor();
  }

  getSection(ref: string): HTMLElement | null {
    return this.sectionsQueryList.find(el => el.nativeElement.id === ref)?.nativeElement || null;
  }

  goToSection(refEl: string, behavior: ScrollBehavior = 'smooth') {
    const section = this.getSection(refEl);

    if (!section) return;

    this.brandMovieService.goToSection(section, behavior, {
      section: refEl,
      movie: this.movie!
    });
  }

  activeIntersectionAnimation() {
    const elements = this.intersections.toArray().map(el => el.nativeElement);
    const els = elements.filter(el => !el.getAttribute('intersectionref'));
    const menuEls = elements.filter(el => el.getAttribute('intersectionref') === 'menu');
    const noThresholdEls = elements.filter(el => el.getAttribute('intersectionref') === 'no-threshold');
    this.observers = [];
    this.observers.push(
      this.intersectionObserve.observe(els, {
        breakpoint: this.desktopBreakpoint,
        threshold: 0.05
      })
    );

    this.observers.push(
      this.intersectionObserve.observe(noThresholdEls, {
        breakpoint: this.desktopBreakpoint,
        threshold: 0
      })
    );

    this.observers.push(
      this.intersectionObserve.observe(menuEls, {
        breakpoint: this.desktopBreakpoint,
        threshold: 0.01,
        visibilityOutput: this.mainMenuVisibleOutput
      })
    );
    // SECTIONS
    const sectionsEls = elements.filter(el => el.getAttribute('intersectionref') === 'section');
    this.observers.push(
      // Observe to update the fragment in the url
      this.intersectionObserve.observe(sectionsEls, {
        threshold: 0.5,
        callback: (entries: IntersectionObserverEntry[]) => {
          entries.forEach(entry => {
            /**
             * The need make this only over desktop breakpoint is because the mobile version
             * the sections don't have a minimum height to be visible in the viewport
             * so the intersection observer will always be intersecting causing the fragment
             * to be updated in the url
             */
            if (entry.isIntersecting && window.innerWidth >= this.desktopBreakpoint) {
              this.router.navigate([], {
                fragment: entry.target.id,
                replaceUrl: true
              });
            }
          });
        }
      })
    );
  }

  playTrailer() {
    this.brandMovieService.closeSidemenu();
    this.videoService.play({
      content: this.movie as Movie,
      category: this.TrackingCategory,
      animate: true,
      videoSettings: { muted: false, autoplay: true }
    });
  }

  setMetaTags(movie?: Movie): void {
    if (!movie) {
      this.metaTagsService.resetMetaTags();
      return;
    }

    //

    const metaTags: MetaTags = {
      url: this.platformLocation.pathname,
      title: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.title`)
        ? `movie.${movie.friendlyUrl}.title`
        : `meta.page.movie-detail.title`,
      description: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.description`)
        ? `movie.${movie.friendlyUrl}.description`
        : `meta.page.movie-detail.description`,

      video: movie.trTrailerLink
    };
    metaTags.favicon = movie?.faviconImageUrl;
    if (movie.tilePhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.tilePhotoUrl, { width: 1280 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.portraitPhotoUrl, { height: 1000 });
    }
    //TODO cases with more directors?
    const director = movie?.movieCrew?.find(mc => {
      if (!mc.crewRole?.name) return false;

      return mc.crewRole.name.toLowerCase() == 'director';
    })?.star?.name;

    this.metaTagsService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
  }

  /**
   * Used to determine if the brand color is readable over the background color and change for a better color if needed
   */

  isRedeableBrandOverBackgroundColor() {
    const brandColor = new TinyColor(this.movie?.settings?.movieSiteBrandColor || '#000000');
    const backgroundColor = new TinyColor(this.movie?.settings?.movieSiteBackgroundColor || '#ffffff');
    return isReadable(brandColor, backgroundColor);
  }

  get trackingEventCalendar() {
    // The 'calendar' prop is added in the calendar action
    return {
      action: 'AddToCalendar',
      properties: {
        label: this.movie!.title!,
        category: 'MovieDetail',
        movie: this.movie!.title!,
        idMovie: this.movie!.id!,
        imdb: this.movie!.imdb!,
        tagsMovie: getMovieTags(this.movie!),
        genresMovie: getMovieGenres(this.movie!)
      }
    };
  }

  get hasShowtimes() {
    return this.movie && this.showtimeStreamingService.determineInCinemasAvailability(this.movie!);
  }

  get hasStreaming() {
    return this.movie && this.showtimeStreamingService.determineStreamingAvailability(this.movie!);
  }

  get MovieShowtimeLink() {
    return window.location.href.replace('/film-info', '/book-now').split('#')[0];
  }

  addBindingStyle(rule: string, value: string) {
    this.bindingStyle[rule] = value;
  }

  removeBindingStyle(rule: string) {
    delete this.bindingStyle[rule];
  }

  setEnvConfiguration() {
    const configs = this.env?.movieSiteConfig;
    if (!configs) return;
    const movie = this.movie?.friendlyUrl || '';
    const config = configs[movie];
    if (!config) return;
    this.setConfigHideTitleLogo(config.awarenessHideTitleLogo);
    this.setConfigHideTitleText(config.awarenessHideTitleText);
    this.setConfigHideReleaseMessage(config.awanessHideReleaseMessage);
    this.setConfigLogoPosition(config.awarenessLogoPosition);
    this.setConfigReleaseMessageAttachToLogo(config.attachReleaseMessageToLogo);
    this.setConfigReleaseMessagePosition(config.releseMessagePosition);
    this.setConfigTileAspectRatio(config.tileImageAspectRatio);
    this.setConfigLandingFillScreen(config?.landingFillScreen);
    this.setConfigLandingDisableGradient(config?.awarenessDisableLandingGradient);
    this.setHeaderBackgroundColor(config.headerBackgroundColor);
    this.setCustomHeaderImages(config.headerImagesConfig);
    this.setDisableMoreInfoElements({
      imdb: config.disableImdb,
      tmdb: config.disableTmdb
    });
  }

  setConfigHideTitleLogo(value?: boolean) {
    if (!value) return;
    this.hideLogoTitle = value;
  }

  setConfigHideTitleText(value?: boolean) {
    if (!value) return;
    this.hideTitleText = value;
  }

  setConfigHideReleaseMessage(value?: boolean) {
    if (!value) return;
    this.hideReleaseMessage = value;
  }

  setConfigLogoPosition(value?: MovieSiteConfigAwarenessLogoPosition) {
    if (!value) return;
    const { horizontal, vertical, offset } = value;
    if (horizontal && (!vertical || vertical == 'bottom')) {
      this.addBindingStyle('--branded-movie-awareness-logo-align-self', HorizontalFlexAlign[horizontal]);
    } else {
      this.addBindingStyle('--branded-movie-awareness-logo-position', 'relative');
      this.addBindingStyle('--branded-movie-awareness-logo-width', '100%');
      this.addBindingStyle('--branded-movie-awareness-logo-height', '100%');
      this.addBindingStyle('--branded-movie-awareness-logo--wrapper-display', 'flex');
      this.addBindingStyle('--branded-movie-awareness-logo--wrapper-width', '100%');
      this.addBindingStyle('--branded-movie-awareness-logo--wrapper-height', '100%');

      if (horizontal) {
        this.addBindingStyle(
          '--branded-movie-awareness-logo--wrapper-justify-content',
          HorizontalFlexAlign[horizontal]
        );
        // this.addBindingStyle('--branded-movie-awareness-logo-left', HorizontalAlign[horizontal]);
      }
      if (vertical) {
        this.addBindingStyle('--branded-movie-awareness-logo--wrapper-align-items', VerticalFlexAlign[vertical]);
        // this.addBindingStyle('--branded-movie-awareness-logo-top', VerticalAlign[vertical]);
      }
      const translateX = horizontal ? HorizontalTranslateX[horizontal] : 0;
      const translateY = vertical ? VerticalTranslateY[vertical] : 0;
      this.addBindingStyle('--branded-movie-awareness-logo-transform', `translate(${translateX}, ${translateY})`);
    }
    if (offset) {
      this.addBindingStyle(
        '--branded-movie-awareness-logo--wrapper-support-transform',
        `translate(${offset.x}, ${offset.y})`
      );
    }
  }

  setConfigReleaseMessageAttachToLogo(value?: boolean) {
    if (!value) return;
    this.attachReleaseMessageToLogo = value;
    this.addBindingStyle('--branded-movie-awareness--releaseMessage-display', value ? 'none' : 'block');
    this.addBindingStyle('--branded-movie-awareness-logo-releaseMessage-display', value ? 'block' : 'none');
  }

  setConfigReleaseMessagePosition(value?: 'left' | 'center' | 'right') {
    if (!value) return;
    this.addBindingStyle('--branded-movie-awareness--releaseMessage-display', 'flex');
    this.addBindingStyle('--branded-movie-awareness--releaseMessage-max-width', '100%');
    this.addBindingStyle('--branded-movie-awareness--releaseMessage-align-self', HorizontalFlexAlign[value]);
  }

  setConfigTileAspectRatio(value?: MovieSiteConfigAspectRatio) {
    if (!value) return;
    const { w, h } = value;
    this.addBindingStyle('--branded-movie-awareness-body-background-top', `calc(100dvw * ${h} / ${w})`);
    this.addBindingStyle('--branded-movie-awareness-background-aspect-ratio', `${w} / ${h}`);
    this.addBindingStyle('--branded-movie-awareness-background-height', 'auto');
    this.addBindingStyle('--branded-movie-landing-height', `calc(100dvw * ${h} / ${w})`);
  }

  setConfigLandingFillScreen(value?: boolean) {
    if (value === undefined) return;
    this.landingFillScreen.set(value);
  }

  setConfigLandingDisableGradient(value?: boolean) {
    if (value === undefined) return;
    this.disableGradient = value;
  }

  setHeaderBackgroundColor(value?: string) {
    if (!value) return;
    this.bindingStyle['--header-background-color'] = value;
  }

  setCustomHeaderImages(config: MovieSiteHeaderImagesConfig | undefined) {
    if (!config) return;
    this.customHeaderImages.set(config.sources?.filter(img => img.filter === undefined || img.filter === 'awareness'));
  }

  setDisableMoreInfoElements(args: { imdb?: boolean; tmdb?: boolean }) {
    const { imdb, tmdb } = args;
    if (imdb !== undefined) {
      this.disableImdb = imdb;
    }
    if (tmdb !== undefined) {
      this.disableTmdb = tmdb;
    }
  }

  get hasAssets() {
    return this.movie?.hasAssets || false;
  }

  ngOnDestroy(): void {
    this.metaTagsService.resetMetaTags();
    this.brandMovieService.removeStyles();
    this.brandMovieService.restoreFooter();

    this.transferState.remove(MOVIE_TRANSFER_KEY);

    if (this.observers) this.intersectionObserve.clean(this.observers);
    this.mainMenuVisibleOutput$?.unsubscribe();
  }
}

export interface SectionHyperLink {
  label: string;
  placeholder: string;
  ref: string | undefined;
}

enum HorizontalFlexAlign {
  left = 'flex-start',
  center = 'center',
  right = 'flex-end'
}

enum VerticalFlexAlign {
  top = 'flex-start',
  middle = 'center',
  bottom = 'flex-end'
}

enum VerticalAlign {
  middle = '50%',
  bottom = '100%'
}

enum VerticalTranslateY {
  middle = '-50%',
  bottom = '0'
}

enum HorizontalAlign {
  left = '0',
  center = '50%',
  right = '100%'
}

enum HorizontalTranslateX {
  left = '0',
  center = '-50%',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  right = '0'
}
